// Pestaña Historial: la bitácora del crédito como timeline vertical, de lo más nuevo a lo más
// viejo. Cada evento sale de una fecha REAL del silo; nada se infiere ni se rellena.
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { cn } from '@/lib/cn';
import { formatTimelineDateMx } from '@/lib/dateHelpers';
import type { LoanDeviceFleetItem } from '@/api/services/loan-credit';
import type { CreditFileBlock } from '../api/useCreditFile';
import { buildCreditHistory, type CreditHistoryKind } from '../logic';
import { CreditCard, CreditEmptyNote } from './CreditFileUi';

const KIND_DOT: Record<CreditHistoryKind, string> = {
    creation: 'bg-info',
    activation: 'bg-success',
    arrears: 'bg-warning',
    block: 'bg-danger',
    scheduledBlock: 'bg-warning',
    settlement: 'bg-success',
};

export interface CreditHistoryTabProps {
    credit: LoanCreditResponse;
    installments: CreditFileBlock<LoanInstallmentResponse[]>;
    device: CreditFileBlock<LoanDeviceFleetItem | null>;
    today: string;
}

export function CreditHistoryTab({ credit, installments, device, today }: CreditHistoryTabProps) {
    const events = buildCreditHistory({
        createdAt: credit.createdAt,
        updatedAt: credit.updatedAt,
        activatedAt: credit.activatedAt,
        status: credit.status,
        financedCapitalCents: credit.financedCapitalCents,
        termWeeks: credit.termWeeks,
        imei: credit.imei,
        installments: installments.data ?? [],
        device: device.data,
        today,
    });

    // El plan y el expediente del equipo son bloques APARTE. Callar que uno falló pintaría un
    // crédito sin su mora o sin su bloqueo como si no los tuviera.
    const failed: string[] = [];
    if (installments.failure) failed.push('el plan de pagos');
    if (device.failure) failed.push('el expediente del equipo');

    return (
        <CreditCard title="Bitácora del crédito" meta={`${String(events.length)} eventos`}>
            {failed.length > 0 ? (
                <p
                    role="status"
                    className="mb-2 rounded bg-warning/10 px-2 py-1.5 text-[10.5px] leading-snug text-foreground"
                >
                    Esta bitácora está incompleta: no se pudieron leer {failed.join(' ni ')}.
                </p>
            ) : null}
            {events.length === 0 ? (
                <CreditEmptyNote>Sin eventos registrados.</CreditEmptyNote>
            ) : (
                <ol className="flex flex-col gap-2.5 border-l-2 border-border pl-3">
                    {events.map((event) => (
                        <li key={event.id} className="relative pl-2.5">
                            <span
                                aria-hidden
                                className={cn(
                                    'absolute -left-[18px] top-1 size-2 rounded-full',
                                    KIND_DOT[event.kind],
                                )}
                            />
                            <div className="text-[10px] tabular-nums text-muted-foreground">
                                {formatTimelineDateMx(event.at)}
                            </div>
                            <div className="mt-px text-[12px] font-semibold">{event.title}</div>
                            <div className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
                                {event.detail}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </CreditCard>
    );
}
